// src/components/SiteSelector.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2, Check } from 'lucide-react';
import { useAuth, Site } from '../contexts/AuthContext';
import './SiteSelector.css';

interface SiteSelectorProps {
  onSiteChange?: (site: Site) => void;
}

const SiteSelector: React.FC<SiteSelectorProps> = ({ onSiteChange }) => {
  const { currentSite, availableSites, setCurrentSite, hasMultipleSites } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>('right');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 280; // min-width of dropdown

      // Check if dropdown would overflow on the left when aligned to right
      if (buttonRect.right - dropdownWidth < 0) {
        setDropdownPosition('left');
      } else {
        setDropdownPosition('right');
      }
    }
  }, [isOpen]);

  const handleSiteSelect = (site: Site) => {
    setCurrentSite(site);
    setIsOpen(false);
    if (onSiteChange) {
      onSiteChange(site);
    }
  };

  // Don't render if only one site
  if (!hasMultipleSites) {
    return null;
  }

  return (
    <div className="site-selector" ref={dropdownRef}>
      <button
        ref={buttonRef}
        className={`site-selector-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Select Site"
      >
        <Building2 size={16} />
        <span className="site-name">{currentSite?.code || 'Select Site'}</span>
        <ChevronDown size={14} className={`chevron ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className={`site-dropdown ${dropdownPosition === 'left' ? 'align-left' : 'align-right'}`}>
          <div className="site-dropdown-header">
            Select Site
          </div>
          <div className="site-list">
            {availableSites.map((site) => (
              <button
                key={site.id}
                className={`site-option ${currentSite?.id === site.id ? 'selected' : ''}`}
                onClick={() => handleSiteSelect(site)}
              >
                <span className="site-option-name">{site.name}</span>
                {currentSite?.id === site.id && (
                  <Check size={16} className="check-icon" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSelector;
